import { useState, useMemo, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { wizardApi } from '@/lib/api';
import { Button, Input, Textarea } from '@solid/ui';
import solidLogo from '@/assets/solid-logo-darkbg.svg';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  message: string;
  hasDocument: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  message?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com',
  'aol.com', 'live.com', 'msn.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me', 'zoho.com', 'mail.com',
  'ymail.com', 'inbox.com', 'gmx.com', 'gmx.net',
]);

function isPersonalEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@')[1];
  return domain ? PERSONAL_EMAIL_DOMAINS.has(domain) : false;
}

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {};

  if (!form.firstName.trim()) {
    errors.firstName = 'First name is required';
  }

  if (!form.lastName.trim()) {
    errors.lastName = 'Last name is required';
  }

  if (!form.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(form.email.trim())) {
    errors.email = 'Please enter a valid email address';
  }

  // Only require project description when they don't have a document
  if (!form.hasDocument && !form.message.trim()) {
    errors.message = 'Please provide a brief project description';
  }

  return errors;
}

function getVisitorId(): string | undefined {
  // Read _ss_vid from cookie (shared across .soliddigital.com subdomains)
  const match = document.cookie.match(/(?:^|;\s*)_ss_vid=([^;]+)/);
  if (match) return match[1];
  // Fallback: check URL param (e.g. WordPress passing it via query string)
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('vid') || undefined;
  } catch {
    return undefined;
  }
}

export default function IntakeForm() {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    message: '',
    hasDocument: true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [personalEmailOverride, setPersonalEmailOverride] = useState(false);

  const showPersonalEmailWarning = useMemo(
    () => isPersonalEmail(form.email) && !personalEmailOverride,
    [form.email, personalEmailOverride],
  );

  // Detect if we're in a sandboxed iframe (blocks form submissions).
  // Computed after hooks so the Rules of Hooks are not violated by an early return.
  const isSandboxed = typeof window !== 'undefined' && window.self !== window.top;

  if (isSandboxed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg animate-fade-in">
          <div className="card p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-heading-3 text-white mb-3">Open in a New Tab</h2>
            <p className="text-body text-slate-400 mb-6">
              This form can't be submitted from a preview window or embedded frame.
            </p>
            <a
              href="https://sales.soliddigital.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-lg font-medium hover:bg-slate-100 transition-colors"
            >
              Open Form in New Tab
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    );
  }

  function handleChange(field: keyof FormState, value: string | boolean) {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (typeof value === 'string' && errors[field as keyof FormErrors]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof FormErrors];
        return next;
      });
    }
    // Reset override if email changes
    if (field === 'email') {
      setPersonalEmailOverride(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Block if personal email without override
    if (showPersonalEmailWarning) return;

    setErrors({});
    setIsLoading(true);

    try {
      const response = await wizardApi.submitIntake({
        formType: 'RFP',
        formData: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          message: form.message.trim(),
        },
        hasDocument: form.hasDocument,
        visitorId: getVisitorId(),
      });

      fetch('/ppilot/info', {                                                                                                                                                                                                   
        method: 'POST',                  
        headers: { 'Content-Type': 'application/json' },                                                                                                                                                                        
        body: JSON.stringify({                                                                                                                                                                                                  
          trackingKey: '98092f9e-51e3-4206-ae82-5a0ba59b8205',                                                                                                                                                                  
          eventType: 'pageview',                                                                                                                                                                                                
          pageUrl: 'https://sales.soliddigital.com/intake-form-submitted',
          referrer: document.referrer || undefined,                                                                                                                                                                                        
          screenWidth: window.screen.width,                 
        }),                                                                                                                                                                                                                     
        keepalive: true,
      });                      

      navigate('/submit/' + response.id);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        'Something went wrong. Please try again.';
      setSubmitError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-12 relative">
      {/* Top-right sign in — hidden for now
      <button
        onClick={handleSignIn}
        className="absolute top-4 right-4 flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/5"
      >
        <LogIn className="w-4 h-4" />
        Sign in
      </button>
      */}

      <div className="w-full max-w-lg animate-fade-in">
        {/* Brand header */}
        <div className="text-center mb-8">
          <a href="https://soliddigital.com" target="_blank" rel="noopener noreferrer">
            <img src={solidLogo} alt="Solid Digital" className="h-8 mx-auto" />
          </a>
          <h1 className="text-heading-2 text-white mt-4">
            Tell us about your project
          </h1>
          <p className="text-body text-slate-400 mt-2 max-w-sm mx-auto">
            Share a few details and we'll get back to you with next steps.
            No commitment required.
          </p>
        </div>

        {/* Form card */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">
              {/* Name row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  placeholder="Jane"
                  required
                  value={form.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  error={errors.firstName}
                  autoComplete="given-name"
                />
                <Input
                  label="Last Name"
                  placeholder="Smith"
                  required
                  value={form.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  error={errors.lastName}
                  autoComplete="family-name"
                />
              </div>

              {/* Email */}
              <Input
                label="Work Email"
                type="email"
                placeholder="jane@company.com"
                required
                value={form.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={errors.email}
                autoComplete="email"
              />

              {/* Personal email warning */}
              {showPersonalEmailWarning && (
                <div className="rounded-lg bg-amber-50 border border-amber-300 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-900">
                        Please use your work email so we can learn about your company and respond faster.
                      </p>
                      <button
                        type="button"
                        onClick={() => setPersonalEmailOverride(true)}
                        className="mt-2 text-xs text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors"
                      >
                        I don't have a work email — continue anyway
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Has document toggle */}
              <label className="flex items-center gap-3 cursor-pointer select-none group">
                <span className="relative inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={form.hasDocument}
                    onChange={(e) => handleChange('hasDocument', e.target.checked)}
                    className="sr-only peer"
                  />
                  <span
                    className="w-10 h-6 rounded-full bg-grey-200 transition-colors duration-200
                               peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 peer-focus-visible:ring-offset-2"
                  />
                  <span
                    className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
                               peer-checked:translate-x-4"
                  />
                </span>
                <span className="text-body-sm text-secondary group-hover:text-secondary-800 transition-colors">
                  I have an RFP or project document to share
                </span>
              </label>

              {form.hasDocument && (
                <p className="text-body-sm text-slate-500 -mt-2">
                  We'll extract project details from your document automatically.
                </p>
              )}

              {/* Company + project description — only when no document */}
              {!form.hasDocument && (
                <>
                  <Input
                    label="Company Name"
                    placeholder="Acme Inc. (optional)"
                    value={form.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    autoComplete="organization"
                  />

                  <Textarea
                    label="Brief Project Description"
                    placeholder="Tell us about your project goals, timeline, and any important details..."
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    error={errors.message}
                  />
                </>
              )}

              {/* Submit error */}
              {submitError && (
                <div className="rounded-lg bg-error/10 border border-error/20 px-4 py-3">
                  <p className="text-body-sm text-error">{submitError}</p>
                </div>
              )}

              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                disabled={showPersonalEmailWarning}
                className="w-full"
              >
                Continue
              </Button>
            </div>
          </form>
        </div>

        {/* Sign in prompt — hidden for now */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-caption text-slate-500">
            By submitting, you agree to be contacted about your project.
            We respect your privacy and will never share your information.
          </p>
        </div>
      </div>
    </div>
  );
}
